const SCRIPT_LOAD_ERROR_RELOAD_KEY = "script-load-error-reload-at";
const SCRIPT_LOAD_ERROR_PARAM = "_scriptLoadErrorTs";
const SCRIPT_LOAD_ERROR_RETRY_COOLDOWN_MS = 300000;

const isScriptElement = (target: EventTarget | null): target is HTMLScriptElement =>
    target instanceof HTMLScriptElement;

const isOwnScriptChunk = (src?: string | null) => {
    if (!src) return false;

    try {
        const url = new URL(src, window.location.href);
        const currentOrigin = new URL(window.location.href).origin;
        return url.origin === currentOrigin && url.pathname.endsWith(".js");
    } catch {
        return false;
    }
};

const isChunkLoadMessage = (message?: string) =>
    !!message &&
    [
        "Loading chunk",
        "Failed to fetch dynamically imported module",
        "Importing a module script failed",
        "error loading dynamically imported module",
    ].some(text => message.includes(text));

const shouldReload = () => {
    if (!navigator.onLine) return false;

    const lastReloadAt = Number(sessionStorage.getItem(SCRIPT_LOAD_ERROR_RELOAD_KEY));
    if (!lastReloadAt) return true;

    return Date.now() - lastReloadAt > SCRIPT_LOAD_ERROR_RETRY_COOLDOWN_MS;
};

const reloadOnce = () => {
    if (!shouldReload()) return;

    sessionStorage.setItem(SCRIPT_LOAD_ERROR_RELOAD_KEY, String(Date.now()));

    const url = new URL(window.location.href);
    url.searchParams.set(SCRIPT_LOAD_ERROR_PARAM, String(Date.now()));
    window.location.replace(url.toString());
};

const registerScriptLoadErrorRefresh = () => {
    const w = window as typeof window & { __scriptLoadErrorRefreshRegistered?: boolean };
    if (w.__scriptLoadErrorRefreshRegistered) return;
    w.__scriptLoadErrorRefreshRegistered = true;

    window.addEventListener("online", () => {
        sessionStorage.removeItem(SCRIPT_LOAD_ERROR_RELOAD_KEY);
    });

    window.addEventListener(
        "error",
        event => {
            if (isScriptElement(event.target) && isOwnScriptChunk(event.target.src)) {
                reloadOnce();
                return;
            }

            if (
                event instanceof ErrorEvent &&
                isChunkLoadMessage(event.message) &&
                isOwnScriptChunk(event.filename || undefined)
            ) {
                reloadOnce();
            }
        },
        true
    );
};

export default registerScriptLoadErrorRefresh;
